import { type ArrayItem, ArraySchema, FileSchema, type FileTypes, OPTIONAL } from "shelving";

/** File extensions for bitmap image types we support. */
export const BITMAP_TYPES = {
	jpeg: "image/jpeg" as const,
	gif: "image/gif" as const,
	png: "image/png" as const,
	webp: "image/webp" as const,
	heif: "image/heif" as const,
	heifs: "image/heif-sequence" as const,
	heic: "image/heic" as const,
	heics: "image/heic-sequence" as const,
	avif: "image/avif" as const,
	avifs: "image/avif-sequence" as const,
} satisfies FileTypes;
export type BitmapExtension = keyof typeof BITMAP_TYPES;
export type BitmapMime = (typeof BITMAP_TYPES)[BitmapExtension];

/** File extensions for vector image types we support. */
export const VECTOR_TYPES = {
	svg: "image/svg+xml" as const,
} satisfies FileTypes;
export type VectorExtension = keyof typeof VECTOR_TYPES;
export type VectorMime = (typeof VECTOR_TYPES)[VectorExtension];

/** File extensions for vector or bitmap image types we support. */
export const IMAGE_TYPES = {
	...VECTOR_TYPES,
	...BITMAP_TYPES,
} satisfies FileTypes;
export type ImageExtension = keyof typeof IMAGE_TYPES;
export type ImageMime = (typeof IMAGE_TYPES)[ImageExtension];

/** File extensions for video image types we support. */
export const VIDEO_TYPES = {
	// Currently none!
} satisfies FileTypes;
export type VideoExtension = keyof typeof VIDEO_TYPES;
export type VideoMime = (typeof VIDEO_TYPES)[VideoExtension];

/** List of file types that can be uploaded in `extension: mime` format. */
export const MEDIA_TYPES = {
	...IMAGE_TYPES,
	...VIDEO_TYPES,
} satisfies FileTypes;
export type MediaExtension = keyof typeof MEDIA_TYPES;
export type MediaMime = (typeof MEDIA_TYPES)[MediaExtension];

/** Maximum size for uploads in MB. */
export const MEDIA_UPLOAD_MAX = 10;

/** List of sizes for bitmap images (these correspond to named transformations in ImageKit). */
export const MEDIA_TRANSFORMS = ["background", "photo", "icon"] as const;
export type MediaTransform = ArrayItem<typeof MEDIA_TRANSFORMS>;

/** Media upload bucket is where user files are uploaded to initially. */
export const MEDIA_UPLOAD_BUCKET_NAME = "trajectory-media-upload";

/** Media source bucket is where user files are copied to once they've been uploaded (used as the origin for ImageKit). */
export const MEDIA_SOURCE_BUCKET_NAME = "trajectory-media-source";

/** ImageKit base URL where images are served from (using source bucket as an origin). */
const IMAGEKIT_URL = "https://ik.imagekit.io/trajectory/";

/** Get the full URL for a media file at a specified width. */
export function getMediaURL(file: string, transform: MediaTransform = "background"): string {
	// Our `MediaSize` types correspond to named transforms in ImageKit.
	return `${IMAGEKIT_URL}/tr:n-${transform}/${file}`;
}

/** Maximum number of images in */
const MEDIA_MAX = 10;

// Schemas.
export const IMAGE = new FileSchema({ title: "Image", placeholder: "No image", types: IMAGE_TYPES });
export const OPTIONAL_IMAGE = OPTIONAL(IMAGE);
export const COVER = new FileSchema({ title: "Cover image", placeholder: "No cover image", types: IMAGE_TYPES });
export const OPTIONAL_COVER = OPTIONAL(COVER);
export const PHOTO = new FileSchema({ title: "Photo", placeholder: "No photo", types: BITMAP_TYPES });
export const OPTIONAL_PHOTO = OPTIONAL(PHOTO);
export const IMAGES = new ArraySchema({ title: "Images", placeholder: "No images", items: IMAGE, max: MEDIA_MAX });
export const REQUIRED_IMAGES = new ArraySchema({ title: "Images", items: IMAGE, min: 1, max: MEDIA_MAX });
